import { ShieldCheck, ShieldAlert, Globe, MailQuestion, MailCheck, MailX, Building2, Landmark, UserCheck, UserSearch, Fingerprint, Sparkles, Gauge } from "lucide-react";

const Badge = ({ ok, warn, icon: Icon, label, testId, title }) => (
  <span
    data-testid={testId}
    title={title}
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border ${
      ok
        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
        : warn
        ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
        : "bg-white/5 border-white/10 text-gray-400"
    }`}
  >
    <Icon className="h-3 w-3" />
    {label}
  </span>
);

const GRADE_TONE = {
  A: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
  B: "bg-sky-500/10 border-sky-400/30 text-sky-200",
  C: "bg-white/5 border-white/15 text-gray-400",
};

const PERSON_SOURCE_LABEL = { apollo: "Found via Apollo.io", pdl: "Found via People Data Labs" };

const emailBadge = (prospect, idx) => {
  const status = prospect.emailStatus || prospect.verification?.email || "none";
  if (!prospect.email) {
    return <Badge icon={MailQuestion} label={status === "invalid" || status === "not_found" ? "Email not found — nothing guessed" : "No email guessed"} testId={`badge-email-${idx}`} />;
  }
  if (status === "verified") return <Badge ok icon={MailCheck} label="Email verified" testId={`badge-email-${idx}`} />;
  if (status === "provider") return <Badge ok icon={MailCheck} label="Email from data provider" testId={`badge-email-${idx}`} />;
  if (status.startsWith("hunter_")) return <Badge warn icon={MailQuestion} label="Email found — not fully verified" testId={`badge-email-${idx}`} />;
  if (status === "pattern_on_resolved_domain") return <Badge warn icon={MailQuestion} label="Email: pattern on real domain" testId={`badge-email-${idx}`} />;
  return <Badge warn icon={MailX} label="Email unverified" testId={`badge-email-${idx}`} />;
};

export const VerificationBadges = ({ prospect, idx = 0 }) => {
  const v = prospect?.verification;
  if (!v) return null;
  const hasUrl = !!prospect.url;
  const linkedinLive = v.linkedinUrl === "live";
  const linkedinOk = linkedinLive || v.linkedinUrl === "verified";
  const registryOk = v.company === "registry";
  const companyResolved = v.company === "resolved";
  const companyParsed = v.company === "parsed";
  const firmOnly = !prospect.personName;
  const grade = prospect.confidence?.grade;

  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`verification-badges-${idx}`}>
      {grade && (
        <span
          data-testid={`badge-confidence-${idx}`}
          title={(prospect.confidence.reasons || []).join(" · ")}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${GRADE_TONE[grade] || GRADE_TONE.C}`}
        >
          <Gauge className="h-3 w-3" />
          Confidence {grade} · {prospect.confidence.label}
        </span>
      )}
      {registryOk ? (
        <Badge
          ok
          icon={Landmark}
          label={`Registry verified · ${prospect.registry?.label || "official record"}`}
          testId={`badge-registry-${idx}`}
          title={prospect.registry?.id ? `Record ${prospect.registry.id}` : undefined}
        />
      ) : (
        <Badge
          ok={companyResolved}
          warn={companyParsed}
          icon={companyResolved ? Globe : Building2}
          label={companyResolved ? "Firm website found" : companyParsed ? "Firm from headline" : "Firm not disclosed"}
          testId={`badge-company-${idx}`}
        />
      )}
      {v.lei && <Badge ok icon={Fingerprint} label="LEI on file" testId={`badge-lei-${idx}`} title={v.lei} />}
      {v.person === "people_data" && (
        <Badge ok icon={UserCheck} label={PERSON_SOURCE_LABEL[prospect.peopleSourceId] || "Found via people-data provider"} testId={`badge-person-${idx}`} />
      )}
      {v.person === "website" && (
        <Badge ok icon={UserSearch} label="Named on firm website (AI-extracted)" testId={`badge-person-${idx}`} />
      )}
      {v.person === "registry" && !hasUrl && (
        <Badge ok icon={UserCheck} label="Contact named on registry record" testId={`badge-person-${idx}`} />
      )}
      {hasUrl && (
        <Badge
          ok={linkedinOk}
          warn={!linkedinOk}
          icon={linkedinOk ? ShieldCheck : ShieldAlert}
          label={linkedinLive ? "LinkedIn live-verified" : linkedinOk ? "LinkedIn verified" : "LinkedIn unverified"}
          testId={`badge-linkedin-${idx}`}
        />
      )}
      {firmOnly ? (
        prospect.inferredBuyer ? (
          <Badge warn icon={Sparkles} label="Buyer role AI-inferred — not verified" testId={`badge-person-${idx}`} title={prospect.inferredBuyer.rationale} />
        ) : (
          <Badge warn icon={UserCheck} label="Decision-maker not yet identified" testId={`badge-person-${idx}`} />
        )
      ) : (
        emailBadge(prospect, idx)
      )}
    </div>
  );
};
